import 'fomantic-ui-less/definitions/modules/calendar.less';
import 'fomantic-ui-less/definitions/modules/checkbox.less';

import 'fomantic-ui-less/definitions/modules/modal';
import 'fomantic-ui-less/definitions/modules/dimmer';
import 'fomantic-ui-less/definitions/modules/transition';
import 'fomantic-ui-less/definitions/modules/calendar';
import 'fomantic-ui-less/definitions/modules/checkbox';
import 'fomantic-ui-less/definitions/behaviors/form';

import ArrowUpSvg from '@mdi/svg/svg/arrow-up.svg';
import ArrowDownSvg from '@mdi/svg/svg/arrow-down.svg';

import {api} from '../Common/fetchWithTimeout';
import isEmpty from 'lodash/isEmpty';
import {parseJSON, stringifyJSON} from '../Common/json';
import {decode} from 'html-entities';
import {createLogger} from '../Common/logger';
import {setupDropdowns} from '../Common/dropdowns';
import {filterFields, formFieldsWithRepeatedValues} from '../Common/formFields';
import {formValidationSelector} from '../Common/formValidation';
import debounce from 'lodash/debounce';
import Sortable, {MultiDrag} from 'sortablejs';
import {botChallengeTokenValues, setupBotChallenges} from './botChallenge';
import {setupRenderReady, setupRootPageReady} from './renderReady';
import {setupCloseableMessages} from '../Common/message';
import {richTextUploadUrlAttribute, setupWorkflowLaunchAttachmentMetadata} from './workflowLaunchAttachments';
import {formFileUploadUrlAttribute, setupFormFileAttachments, waitForFormFileAttachmentUploads} from './formFileAttachments';
import {setupRenderedPageComponents} from '../Common/renderedPageComponents';
import './styles/workflowTaskGrid.css';

const LOGGER = createLogger("PageView");

const currentPageId = document.querySelector('meta[name="swf-page-id"]')?.getAttribute('content') || '';
const cardIndexFieldName = 'swf:cardIndex';
const taskListTableSelector = 'table[data-id][data-tasks]';
const taskGridColumnSelector = 'div.column[data-id][data-tasks]';
const taskGridCardSelector = '.ui.card[data-workflow-id][data-task-id]';
const taskGridDraggableSelector = '.ui.card.draggable[data-workflow-id][data-task-id]';
const taskGridSelectedCardClass = 'swf-task-card-selected';
const taskItemIdentifierReferenceAttributes = ['for', 'input', 'form', 'list'];
const taskItemIdentifierReferenceListAttributes = ['aria-labelledby', 'aria-describedby', 'aria-controls', 'aria-owns'];
const noopApi = () => Promise.resolve({});

Sortable.mount(new MultiDrag());

const findAll = (root, selector) => [
    ...(root.matches?.(selector) ? [root] : []),
    ...(root.querySelectorAll?.(selector) || []),
];

const markInitialized = (element, name) => {
    const attribute = `data-page-view-${name}`;
    if (element.hasAttribute(attribute)) {
        return false;
    }

    element.setAttribute(attribute, 'true');
    return true;
};

const isTaskFormComponent = element => !!element.closest?.('.markdown-body.task form');

const isTaskListTableComponent = element => !!element.closest?.(taskListTableSelector);

const isTaskGridColumnComponent = element => !!element.closest?.(taskGridColumnSelector);

const isTaskTemplateComponent = element => isTaskListTableComponent(element) || isTaskGridColumnComponent(element);

const namespaceIdentifiers = (root, namespace) => {
    const identifiers = new Map();
    const tabs = new Map();

    findAll(root, '[id]').forEach(element => {
        const identifier = element.id;
        if (!identifier) {
            return;
        }

        const namespacedIdentifier = `${namespace}-${identifier}`;
        identifiers.set(identifier, namespacedIdentifier);
        element.id = namespacedIdentifier;
    });

    const replaceIdentifier = identifier => identifiers.get(identifier) || identifier;
    taskItemIdentifierReferenceAttributes.forEach(attribute => {
        findAll(root, `[${attribute}]`).forEach(element => {
            const identifier = element.getAttribute(attribute);
            if (identifiers.has(identifier)) {
                element.setAttribute(attribute, replaceIdentifier(identifier));
            }
        });
    });
    taskItemIdentifierReferenceListAttributes.forEach(attribute => {
        findAll(root, `[${attribute}]`).forEach(element => {
            const identifiersValue = element.getAttribute(attribute);
            if (identifiersValue) {
                element.setAttribute(attribute, identifiersValue.split(/\s+/).map(replaceIdentifier).join(' '));
            }
        });
    });

    findAll(root, '[data-tab]').forEach(element => {
        const tab = element.getAttribute('data-tab');
        if (!tab) {
            return;
        }

        const namespacedTab = tabs.get(tab) || `${namespace}-${tab}`;
        tabs.set(tab, namespacedTab);
        element.setAttribute('data-tab', namespacedTab);
    });
};

const namespaceTaskTemplateIdentifiers = (taskTemplate, componentInstance) =>
    namespaceIdentifiers(taskTemplate, `swf-page-template-${componentInstance}`);

const namespaceTaskItemIdentifiers = (taskItem, componentInstance) =>
    namespaceIdentifiers(
        taskItem,
        `swf-page-task-${componentInstance}-${taskItem.getAttribute('data-task-id')}`);

export const createTaskGridSelectionState = () => ({
    dragColumns: [],
    dragCardOrigins: new Map(),
});

const taskGridCardColumn = card => card.closest?.(taskGridColumnSelector);

export const taskGridCards = taskListColumn => [
    ...taskListColumn.querySelectorAll(taskGridCardSelector),
].filter(card => taskGridCardColumn(card) === taskListColumn && !card.classList.contains('hidden'));

export const taskGridCardIds = taskListColumn => taskGridCards(taskListColumn)
    .map(card => card.getAttribute('data-task-id'));

const uniqueElements = elements => [...new Set(elements.filter(Boolean))];

const taskGridColumns = grid => [...grid.querySelectorAll(taskGridColumnSelector)];

const sortedTaskGridCards = (grid, cards) => {
    const selected = new Set(cards);
    return taskGridColumns(grid)
        .flatMap(taskGridCards)
        .filter(card => selected.has(card));
};

const taskGridDraggedCards = event => {
    const selectedCards = taskGridCards(event.from)
        .filter(card => card.classList.contains(taskGridSelectedCardClass));
    const cards = event.items?.length ? event.items : selectedCards.length ? selectedCards : [event.item];
    return sortedTaskGridCards(event.from.closest('div.ui.grid') || event.from, cards);
};

const setupTaskGridCard = card => {
    card.classList.add('swf-task-grid-card');
    card.classList.add('draggable');
    card.setAttribute('draggable', 'true');
    card.querySelector('.swf-task-card-controls')?.remove();
};

export const setupTaskGridCards = (taskListColumn, state) => {
    taskGridCards(taskListColumn).forEach(setupTaskGridCard);
    state.dragCardOrigins = new Map([...state.dragCardOrigins]
        .filter(([card]) => card.isConnected));
};

export const startTaskGridCardDrag = (state, event) => {
    const dragCards = taskGridDraggedCards(event);
    state.dragCardOrigins = new Map(dragCards.map(card => [card, taskGridCardColumn(card)]));
    state.dragColumns = uniqueElements([...state.dragCardOrigins.values()]);
    return dragCards;
};

const taskGridCardInputPatch = (card, input, apiClient = api) => {
    const workflowId = card.getAttribute('data-workflow-id');
    const taskId = card.getAttribute('data-task-id');
    return apiClient(`/api/workflows/${workflowId}/tasks/${taskId}/input`, {
        method: 'PATCH',
        headers: {
            'Content-Type': 'application/json'
        }, body: stringifyJSON(input)
    }).catch(error => LOGGER.error('Unable to save task card input', {workflowId, taskId, error}));
};

export const persistTaskGridColumnOrder = (taskListColumn, apiClient = api) => taskGridCards(taskListColumn)
    .map((card, index) => taskGridCardInputPatch(card, {[cardIndexFieldName]: index}, apiClient));

export const persistTaskGridDestinationInput = (cards, taskListColumn, apiClient = api) => {
    const columnInputParameters = parseJSON(decode(taskListColumn.getAttribute('data-input-parameters'))) || {};
    if (isEmpty(columnInputParameters)) {
        return [];
    }
    return cards.map(card => taskGridCardInputPatch(card, columnInputParameters, apiClient));
};

const updateTaskGridColumnCount = taskListColumn => {
    const countCell = taskListColumn.querySelector('.swf-task-grid-count');
    if (countCell) {
        countCell.textContent = `${taskGridCards(taskListColumn).length}`;
    }
};

export const finishTaskGridCardDrop = ({state, event, from, to, apiClient = api}) => {
    const movedCards = taskGridDraggedCards(event);
    const affectedColumns = uniqueElements([
        from,
        to,
        ...state.dragColumns,
        ...movedCards.map(taskGridCardColumn),
    ]);
    const destinationCards = movedCards.filter(card => (state.dragCardOrigins.get(card) || from) !== taskGridCardColumn(card));

    if (destinationCards.length) {
        persistTaskGridDestinationInput(destinationCards, to, apiClient);
    }

    affectedColumns.forEach(taskListColumn => {
        persistTaskGridColumnOrder(taskListColumn, apiClient);
        updateTaskGridColumnCount(taskListColumn);
    });

    state.dragColumns = [];
    state.dragCardOrigins = new Map();

    return {
        movedCards,
        affectedColumns,
    };
};

const setupPageViewDropdowns = root => {
    findAll(root, '.ui.dropdown')
        .filter(element => !isTaskFormComponent(element))
        .filter(element => !isTaskTemplateComponent(element))
        .forEach(setupDropdowns);
};

const setupTaskContentComponents = root => {
    const ownerDocument = root.ownerDocument || document;

    findAll(root, '[data-workflow-id][data-task-id]')
        .filter(taskItem => !taskItem.hasAttribute('data-swf-task-template'))
        .forEach(taskItem => {
            const uploadUrl = `/workflows/${taskItem.getAttribute('data-workflow-id')}/tasks/${taskItem.getAttribute('data-task-id')}/attachments`;
            findAll(taskItem, 'file-attachment').forEach(fileAttachment =>
                fileAttachment.setAttribute(formFileUploadUrlAttribute, uploadUrl));
            findAll(taskItem, 'textarea.rich-text').forEach(richText =>
                richText.setAttribute(richTextUploadUrlAttribute, uploadUrl));
        });
    setupBotChallenges(root, ownerDocument);
    setupDropdowns(root);
    setupFormFileAttachments(root);
    $(root.querySelectorAll('.ui.calendar')).calendar({
        today: true
    });
    $(root.querySelectorAll('.ui.checkbox')).checkbox();
    setupCloseableMessages(root);
    setupRenderedPageComponents(root);
};

const setupPageViewFormFileAttachments = root =>
    findAll(root, 'file-attachment')
        .filter(fileAttachment => !isTaskTemplateComponent(fileAttachment))
        .forEach(fileAttachment => setupFormFileAttachments(fileAttachment));

export const setupFomanticComponents = (root = document) => {
    setupPageViewDropdowns(root);
    $(findAll(root, '.ui.calendar')
        .filter(element => !isTaskFormComponent(element))
        .filter(element => !isTaskTemplateComponent(element))).calendar({
        today: true
    });
    $(findAll(root, '.ui.checkbox')
        .filter(element => !isTaskFormComponent(element))
        .filter(element => !isTaskTemplateComponent(element))).checkbox();
    $(findAll(root, '.ui.menu .item[data-tab]')).tab({
        duration: 0
    });
    setupCloseableMessages(root);
};

const updateTask = debounce(($form, row) => {
    if (!$form.form('is valid')) {
        return;
    }

    const workflowId = row.getAttribute('data-workflow-id');
    const taskId = row.getAttribute('data-task-id');
    waitForFormFileAttachmentUploads($form[0])
        .then(() => {
            const filteredOutput = filterFields(
                formFieldsWithRepeatedValues($form.form('get values'), $form[0]));
            const botChallengeTokens = botChallengeTokenValues($form[0]);
            const output = isEmpty(botChallengeTokens)
                ? filteredOutput
                : {...filteredOutput, 'swf:botChallenge': botChallengeTokens};
            if (!isEmpty(output)) {
                api(`/api/workflows/${workflowId}/tasks/${taskId}/output`, {
                    method: 'PATCH',
                    headers: {
                        'Content-Type': 'application/json',
                    },
                    body: stringifyJSON(output),
                }).catch(error => LOGGER.error('Unable to save task', {workflowId, taskId, error}));
            }
        })
        .catch(error => LOGGER.error('Unable to wait for task attachment uploads', {workflowId, taskId, error}));
}, 1000);

export const setupPageView = (root = document.querySelector('main.page-content') || document) => {
    const ownerDocument = root.ownerDocument || root;

    setupBotChallenges(root, ownerDocument);
    setupFomanticComponents(root);
    setupWorkflowLaunchAttachmentMetadata(root);
    setupPageViewFormFileAttachments(root);
    setupRenderedPageComponents(root, {setupTabs: false, isExcluded: isTaskTemplateComponent});

    findAll(root, taskListTableSelector).forEach((taskListTable, tableIndex) => {
        if (!markInitialized(taskListTable, 'task-table')) {
            return;
        }

        const workflowDefinitionId = taskListTable.getAttribute('data-id');
        const workflowDefinitionVersion = taskListTable.getAttribute('data-version');
        const taskNames = parseJSON(decode(taskListTable.getAttribute('data-tasks')));
        const filterJsonPath = parseJSON(decode(taskListTable.getAttribute('data-filter')));
        const defaultSort = parseJSON(decode(taskListTable.getAttribute('data-sort')));
        const defaultDesc = taskListTable.getAttribute('data-desc') === '1';
        const pageSize = taskListTable.getAttribute('data-size');
        const tableWidth = taskListTable.getAttribute('data-width');
        const tableControlId = `data-table-${tableIndex}`;
        const sortableOrderId = `${tableControlId}-sortable-order`;
        const refreshButtonId = `${tableControlId}-refresh`;
        const firstPageButtonId = `${tableControlId}-first-page`;
        const previousPageButtonId = `${tableControlId}-previous-page`;
        const nextPageButtonId = `${tableControlId}-next-page`;

        const tableRows = [];
        let foundDataRow = -1;
        const taskListBody = taskListTable.querySelector('tbody');
        taskListBody.querySelectorAll('tr').forEach((tableRow, rowIndex) => {
            if (foundDataRow < 0 && tableRow.hasAttribute('data-workflow-id') && tableRow.hasAttribute('data-task-id')) {
                foundDataRow = rowIndex;
            }

            if (foundDataRow < 0) {
                tableRow.classList.remove('hidden');
            } else {
                tableRow.classList.add('hidden');
                namespaceTaskTemplateIdentifiers(tableRow, `table-${tableIndex}`);
                const clonedRow = tableRow.cloneNode(true);
                clonedRow.classList.remove('hidden');
                tableRows.push(clonedRow.outerHTML);
            }
        });

        if (!isEmpty(tableRows)) {
            taskListBody.querySelectorAll("tr th a[href*='sort=']").forEach(sortLink => {
                sortLink.classList.add('sortable');
                const urlSearchParams = new URL(sortLink.getAttribute('href'), window.location.origin).searchParams;
                urlSearchParams.set('wdf', workflowDefinitionId);

                if (!isEmpty(workflowDefinitionVersion)) {
                    urlSearchParams.set('wdfv', workflowDefinitionVersion);
                }

                let tableSortDesc = urlSearchParams.has('desc');
                if (tableSortDesc || defaultDesc) {
                    urlSearchParams.delete('desc');
                } else {
                    urlSearchParams.set('desc', '1');
                }

                sortLink.addEventListener('click', event => {
                    event.preventDefault();
                    taskListTable.querySelector(`#${sortableOrderId}`)?.remove();

                    tableSortDesc = urlSearchParams.has('desc');

                    history.pushState(null, '', `${window.location.pathname}?${urlSearchParams.toString()}`);

                    if (tableSortDesc) {
                        urlSearchParams.delete('desc');
                    } else {
                        urlSearchParams.set('desc', '1');
                    }
                    sortLink.href = `${window.location.pathname}?${urlSearchParams.toString()}`;

                    sortLink.insertAdjacentHTML('beforeend', `<img id='${sortableOrderId}' class="icon-14 ui icon" data-variation="mini" data-tooltip="${tableSortDesc ? 'Sorted descending' : 'Sorted ascending'}" src="${tableSortDesc ? ArrowDownSvg : ArrowUpSvg}" alt=""/>`);

                    createTableContent(null, null, event.currentTarget);
                });

                const pageSearchParams = new URLSearchParams(window.location.search);
                const pageWorkflowDefinitionId = pageSearchParams.get('wdf') || workflowDefinitionId;
                const pageSort = pageSearchParams.get('sort') || defaultSort;
                const pageDesc = pageSearchParams.has('desc') || defaultDesc;

                if (pageWorkflowDefinitionId === workflowDefinitionId && pageSort === urlSearchParams.get('sort')) {
                    sortLink.href = `${window.location.pathname}?${urlSearchParams.toString()}`;
                    sortLink.insertAdjacentHTML('beforeend', `<img id='${sortableOrderId}' class="icon-14 ui icon" src="${pageDesc ? ArrowDownSvg : ArrowUpSvg}" alt=""/>`);
                }
            });

            taskListTable.insertAdjacentHTML('beforeend', `<tfoot class="full-width"><tr><th colSpan="${tableWidth}" class="right aligned">
          <div class="ui buttons basic tiny left floated"><button id="${refreshButtonId}" class="ui button">Refresh</button></div>
          <div class="ui icon buttons basic tiny paging">
          <div id="${firstPageButtonId}" class="ui button disabled"><i class="angle double left icon"></i></div>
          <div id="${previousPageButtonId}" class="ui button disabled"><i class="angle left icon"></i></div>
          <div id="${nextPageButtonId}" class="ui button disabled"><i class="angle right icon"></i></div>
          </div>
          </th></tr></tfoot>`);

            taskListTable.querySelector(`#${refreshButtonId}`).addEventListener('click', event => {
                event.preventDefault();
                createTableContent(null, null, event.currentTarget);
            });

            api(`/api/workflow-definitions/${workflowDefinitionId}/enabled`, {
                method: 'GET'
            }).then(() => {
                const pageRedirectParams = new URLSearchParams();

                if (!isEmpty(workflowDefinitionVersion)) {
                    pageRedirectParams.set('version', workflowDefinitionVersion);
                }

                pageRedirectParams.set('page', currentPageId);

                const pageSearchParams = new URLSearchParams(window.location.search);
                const pageWorkflowDefinitionId = pageSearchParams.get('wdf');
                const sort = pageWorkflowDefinitionId === workflowDefinitionId ? pageSearchParams.get('sort') : null;
                const desc = pageWorkflowDefinitionId === workflowDefinitionId ? pageSearchParams.has('desc') : null;

                if (sort) {
                    pageRedirectParams.set('sort', sort);
                }

                if (desc) {
                    pageRedirectParams.set('desc', '1');
                }

                taskListTable.querySelector(`#${refreshButtonId}`).insertAdjacentHTML('beforebegin',
                    `<a class="ui button primary basic" href="/workflow-definitions/${workflowDefinitionId}/start?${pageRedirectParams.toString()}">Create</a> `);
            }).catch(error => LOGGER.debug(`Unable to confirm if ${workflowDefinitionId} enabled`, error));

            let tableLoading = false;
            let tableError;
            const createTableContent = (previousCursor, nextCursor, loadingControl) => {
                if (tableLoading) {
                    return;
                }
                tableLoading = true;
                tableError?.remove();

                const loadingControlContent = loadingControl?.innerHTML;
                const loadingControlClassName = loadingControl?.className;
                const loadingControlDisabled = loadingControl instanceof HTMLButtonElement ? loadingControl.disabled : null;
                let loadingControlIcon;
                let loadingIndicator;
                const controlStates = loadingControl && !(loadingControl instanceof HTMLButtonElement) ?
                    [...taskListTable.querySelectorAll('tfoot .ui.button, tbody th a.sortable')]
                    .map(control => ({
                        control,
                        disabled: control.classList.contains('disabled'),
                        ariaDisabled: control.getAttribute('aria-disabled'),
                        nativeDisabled: control instanceof HTMLButtonElement ? control.disabled : null,
                    })) : [];
                controlStates.forEach(({control}) => {
                    control.classList.add('disabled');
                    control.setAttribute('aria-disabled', 'true');
                    if (control instanceof HTMLButtonElement) {
                        control.disabled = true;
                    }
                });

                if (loadingControl) {
                    if (loadingControl instanceof HTMLButtonElement) {
                        loadingControl.disabled = true;
                        loadingControl.classList.add('disabled');
                        loadingControl.innerHTML = '<i class="loading spinner icon"></i>';
                    } else if (loadingControl instanceof HTMLAnchorElement) {
                        loadingControlIcon = loadingControl.querySelector('.icon')?.outerHTML;
                        loadingControl.querySelector('.icon')?.remove();
                        loadingIndicator = document.createElement('i');
                        loadingIndicator.className = 'loading spinner icon';
                        loadingIndicator.style.marginLeft = '5px';
                        loadingControl.insertAdjacentElement('afterend', loadingIndicator);
                    } else {
                        loadingControl.querySelector('.icon')?.remove();
                        loadingControl.insertAdjacentHTML('afterbegin', '<i class="loading spinner icon"></i>');
                    }
                } else {
                    taskListBody.insertAdjacentHTML('beforeend', `<tr data-page-task-initial-loader="true"><td${tableWidth ? ` colSpan="${tableWidth}"` : ''} class="center aligned"><div class="ui active inline loader"></div></td></tr>`);
                }

                const pageSearchParams = new URLSearchParams(window.location.search);
                const pageWorkflowDefinitionId = pageSearchParams.get('wdf');
                const sort = pageWorkflowDefinitionId === workflowDefinitionId ? pageSearchParams.get('sort') : null;
                const desc = pageWorkflowDefinitionId === workflowDefinitionId ? pageSearchParams.has('desc') : null;

                api(`/api/workflow-definitions/${workflowDefinitionId}/tasks`, {
                    method: 'POST',
                    headers: {
                        'Content-Type': 'application/json',
                    },
                    body: stringifyJSON({
                        version: isEmpty(workflowDefinitionVersion) ? null : workflowDefinitionVersion,
                        taskNames,
                        filterJsonPath,
                        taskTemplate: tableRows.join(''),
                        page: currentPageId,
                        sort: isEmpty(sort) ? defaultSort : sort,
                        desc: desc == null ? defaultDesc : desc,
                        size: Number(pageSize) || null,
                        previousCursor,
                        nextCursor
                    })
                }).then(({content: taskList, previous: previousCursor, next: nextCursor}) => {
                    taskListBody.querySelectorAll('tr').forEach((tableRow, rowIndex) => {
                        if (rowIndex >= foundDataRow) {
                            tableRow.remove();
                        }
                    });

                    if (!isEmpty(taskList)) {
                        taskListBody.insertAdjacentHTML('beforeend', taskList.join(''));
                        taskListBody.querySelectorAll('tr[data-workflow-id][data-task-id]').forEach(row =>
                            namespaceTaskItemIdentifiers(row, `table-${tableIndex}`));
                        setupTaskContentComponents(taskListBody);
                        taskListBody.querySelectorAll('tr[data-workflow-id][data-task-id]').forEach(row => {
                            const $form = $(row).form({
                                on: 'change',
                                autoCheckRequired: true,
                                selector: formValidationSelector,
                                duration: 0,
                                onValid: function () {
                                    updateTask($form, row);
                                }
                            });
                        })
                    } else {
                        taskListBody.insertAdjacentHTML('beforeend', `<tr><td${tableWidth ? ` colSpan="${tableWidth}"` : ''}>You don't have any tasks.</td></tr>`);
                    }

                    const footer = taskListTable.querySelector('tfoot th');
                    footer.querySelector('.paging')?.remove();
                    footer.insertAdjacentHTML('beforeend', `
          <div class="ui icon buttons basic tiny paging">
${!isEmpty(previousCursor) ? `<div id="${firstPageButtonId}" class="ui button"><i class="angle double left icon"></i></div><div id="${previousPageButtonId}" class="ui button"><i class="angle left icon"></i></div>` : `<div id="${firstPageButtonId}" class="ui button disabled"><i class="angle double left icon"></i></div><div id="${previousPageButtonId}" class="ui button disabled"><i class="angle left icon"></i></div>`}
${!isEmpty(nextCursor) ? `<div id="${nextPageButtonId}" class="ui button"><i class="angle right icon"></i></div>` : `<div id="${nextPageButtonId}" class="ui button disabled"><i class="angle right icon"></i></div>`}
          </div>
        `);
                    taskListTable.querySelector(`#${firstPageButtonId}`)?.addEventListener('click', event =>
                        createTableContent(null, null, event.currentTarget));
                    taskListTable.querySelector(`#${previousPageButtonId}`)?.addEventListener('click', event =>
                        createTableContent(previousCursor, null, event.currentTarget));
                    taskListTable.querySelector(`#${nextPageButtonId}`)?.addEventListener('click', event =>
                        createTableContent(null, nextCursor, event.currentTarget));
                }).catch(() => {
                    taskListBody.querySelector('[data-page-task-initial-loader]')?.remove();
                    tableError = document.createElement('div');
                    tableError.className = 'ui error message';
                    tableError.textContent = 'Error loading workflow tasks. Please try again.';
                    taskListTable.querySelector('tfoot th').prepend(tableError);
                }).finally(() => {
                    controlStates.forEach(({control, disabled, ariaDisabled, nativeDisabled}) => {
                        if (!control.isConnected) {
                            return;
                        }
                        control.classList.toggle('disabled', disabled);
                        if (ariaDisabled == null) {
                            control.removeAttribute('aria-disabled');
                        } else {
                            control.setAttribute('aria-disabled', ariaDisabled);
                        }
                        if (control instanceof HTMLButtonElement) {
                            control.disabled = nativeDisabled;
                        }
                    });
                    if (loadingControl?.isConnected) {
                        loadingControl.className = loadingControlClassName;
                        if (loadingControl instanceof HTMLAnchorElement) {
                            loadingIndicator?.remove();
                            if (loadingControlIcon) {
                                loadingControl.insertAdjacentHTML('beforeend', loadingControlIcon);
                            }
                        } else {
                            loadingControl.innerHTML = loadingControlContent;
                            loadingControl.disabled = loadingControlDisabled;
                        }
                    }
                    tableLoading = false;
                });
            }

            createTableContent();
        }
    });

    const grids = findAll(root, 'div.ui.grid');
    let taskGridIndex = 0;
    grids.forEach(grid => {
        const taskColumns = grid.querySelectorAll('div.column[data-id][data-tasks]');
        if (!taskColumns.length) {
            return;
        }

        const gridIndex = taskGridIndex++;
        const taskGridSelectionState = createTaskGridSelectionState();

        taskColumns.forEach((taskListColumn, columnIndex) => {
            if (!markInitialized(taskListColumn, 'task-grid-column')) {
                return;
            }

            const workflowDefinitionId = taskListColumn.getAttribute('data-id');
            const workflowDefinitionVersion = taskListColumn.getAttribute('data-version');
            const color = taskListColumn.getAttribute('data-color');
            const taskNames = parseJSON(decode(taskListColumn.getAttribute('data-tasks')));
            const filterJsonPath = parseJSON(decode(taskListColumn.getAttribute('data-filter')));
            const inputParameters = parseJSON(decode(taskListColumn.getAttribute('data-input-parameters'))) || {};
            const pageSize = taskListColumn.getAttribute('data-size');

            const cards = [];
            let foundDataCard = -1;
            taskListColumn.querySelectorAll('.ui.card').forEach((card, cardIndex) => {
                if (foundDataCard < 0 && card.hasAttribute('data-workflow-id') && card.hasAttribute('data-task-id')) {
                    foundDataCard = cardIndex;
                }

                if (foundDataCard < 0) {
                    card.classList.remove('hidden');
                } else {
                    card.classList.add('hidden');
                    namespaceTaskTemplateIdentifiers(card, `grid-${gridIndex}-column-${columnIndex}`);
                    const clonedCard = card.cloneNode(true);
                    clonedCard.classList.remove('hidden');
                    cards.push(clonedCard.outerHTML);
                }
            });

            if (!isEmpty(cards)) {
                taskListColumn.classList.add('swf-workflow-task-grid-column');
                const taskGridContent = document.createElement('div');
                taskGridContent.className = 'ui long scrolling basic segment swf-workflow-task-grid-content';
                taskListColumn.querySelectorAll('.ui.card').forEach(card => taskGridContent.append(card));
                taskListColumn.append(taskGridContent);
                const refreshButtonId = `data-grid-${gridIndex}-${columnIndex}-refresh`;
                taskListColumn.insertAdjacentHTML('beforeend',
                    `<div class="ui fitted basic segment swf-task-grid-controls"><button id="${refreshButtonId}" class="ui mini basic icon button right floated" aria-label="Refresh"><i class="refresh icon"></i></button></div>`);
                const gridControls = taskListColumn.querySelector('.swf-task-grid-controls');

                taskListColumn.querySelector(`#${refreshButtonId}`).addEventListener('click', event => {
                    event.preventDefault();
                    createGridColumnContent(event.currentTarget);
                });

                api(`/api/workflow-definitions/${workflowDefinitionId}/enabled${isEmpty(workflowDefinitionVersion) ? '' : `?version=${workflowDefinitionVersion}`}`, {
                    method: 'GET'
                }).then(() => {
                    const pageRedirectParams = new URLSearchParams(inputParameters);
                    Object.keys(inputParameters).forEach(name => {
                        const value = inputParameters[name];

                        if (!isEmpty(name) && !isEmpty(value) && value instanceof Object) {
                            pageRedirectParams.set(name, stringifyJSON(value));
                        }
                    });

                    if (!isEmpty(workflowDefinitionVersion)) {
                        pageRedirectParams.set('version', workflowDefinitionVersion);
                    }

                    pageRedirectParams.set('page', currentPageId);

                    taskListColumn.querySelector(`#${refreshButtonId}`).insertAdjacentHTML('beforebegin',
                        `<a class="ui mini primary basic button labeled icon" href="/workflow-definitions/${workflowDefinitionId}/start?${pageRedirectParams.toString()}"><i class="plus icon"></i>Create</a>`);
                }).catch(error => LOGGER.debug(`Unable to confirm if ${workflowDefinitionId} enabled`, error));

                let taskCountCell;
                let gridLoading = false;
                let gridError;
                let initialGridLoader;
                const createGridColumnContent = loadingControl => {
                    if (gridLoading) {
                        return;
                    }
                    gridLoading = true;
                    gridError?.remove();

                    const loadingControlContent = loadingControl?.innerHTML;
                    const loadingControlClassName = loadingControl?.className;
                    if (loadingControl) {
                        loadingControl.disabled = true;
                        loadingControl.classList.add('disabled');
                        loadingControl.innerHTML = '<i class="loading spinner icon"></i>';
                    } else {
                        initialGridLoader = document.createElement('div');
                        initialGridLoader.className = 'ui active centered inline loader';
                        initialGridLoader.setAttribute('data-page-task-initial-loader', 'true');
                        taskGridContent.append(initialGridLoader);
                    }

                    const createGridSortable = persistTaskChanges => {
                        taskListColumn.__swfTaskGridSortable?.destroy?.();
                        taskListColumn.__swfTaskGridSortable = new Sortable(taskGridContent, {
                            group: `tasks-${gridIndex}`,
                            dataIdAttr: 'data-task-id',
                            draggable: taskGridDraggableSelector,
                            filter: 'a, button, input, select, textarea, label, [contenteditable="true"], .ui.dropdown, .ui.checkbox, .ui.calendar',
                            preventOnFilter: false,
                            multiDrag: true,
                            selectedClass: taskGridSelectedCardClass,
                            chosenClass: 'chosen',
                            ghostClass: 'ghost',
                            fallbackClass: 'swf-task-grid-fallback',
                            direction: 'vertical',
                            fallbackOnBody: true,
                            fallbackTolerance: 3,
                            animation: 150,
                            onStart: function (evt) {
                                startTaskGridCardDrag(taskGridSelectionState, {
                                    ...evt,
                                    from: evt.from.closest(taskGridColumnSelector),
                                });
                            },
                            onEnd: function (evt) {
                                const from = evt.from.closest(taskGridColumnSelector);
                                const to = evt.to.closest(taskGridColumnSelector);
                                finishTaskGridCardDrop({
                                    state: taskGridSelectionState,
                                    event: {...evt, from},
                                    from,
                                    to,
                                    apiClient: persistTaskChanges ? api : noopApi,
                                });
                            },
                        });
                    };

                    const renderGridColumnContent = (taskList, persistTaskChanges) => {
                        taskGridContent.querySelectorAll('.ui.card').forEach((card, cardIndex) => {
                            if (cardIndex >= foundDataCard) {
                                card.remove();
                            }
                        });
                        taskCountCell?.remove();

                        let taskCount = 0;

                        if (!isEmpty(taskList)) {
                            taskGridContent.insertAdjacentHTML('beforeend', taskList.join(''));
                            taskGridCards(taskListColumn).forEach(card =>
                                namespaceTaskItemIdentifiers(card, `grid-${gridIndex}-column-${columnIndex}`));
                            taskCount = taskList?.length || 0;
                        }

                        taskCountCell = document.createElement('div');
                        taskCountCell.className = `left aligned floating ui circular label swf-task-grid-count${color ? ` ${color}` : ''}`;
                        taskCountCell.textContent = `${taskCount}`;
                        taskListColumn.insertAdjacentElement('afterbegin', taskCountCell);

                        setupTaskGridCards(taskListColumn, taskGridSelectionState);
                        setupTaskContentComponents(taskListColumn);
                        createGridSortable(persistTaskChanges);
                    };

                    api(`/api/workflow-definitions/${workflowDefinitionId}/tasks`, {
                        method: 'POST',
                        headers: {
                            'Content-Type': 'application/json',
                        },
                        body: stringifyJSON({
                            version: isEmpty(workflowDefinitionVersion) ? null : workflowDefinitionVersion,
                            taskNames,
                            filterJsonPath,
                            taskTemplate: cards.join(''),
                            page: currentPageId,
                            sort: cardIndexFieldName,
                            size: Number(pageSize) || null
                        })
                    }).then(({content: taskList}) => {
                        initialGridLoader?.remove();
                        renderGridColumnContent(taskList, true);
                    }).catch(error => {
                        initialGridLoader?.remove();
                        if (taskListColumn.getAttribute('data-dummy-tasks') === 'true' &&
                            error?.status !== 400) {
                            renderGridColumnContent(cards, false);
                            return;
                        }

                        gridError = document.createElement('div');
                        gridError.className = 'ui error message';
                        gridError.textContent = 'Error loading workflow tasks. Please try again.';
                        gridControls.insertAdjacentElement('afterend', gridError);
                    }).finally(() => {
                        if (loadingControl?.isConnected) {
                            loadingControl.disabled = false;
                            loadingControl.className = loadingControlClassName;
                            loadingControl.innerHTML = loadingControlContent;
                        }
                        gridLoading = false;
                    });
                }

                createGridColumnContent();
            }
        });
    });

};

$(document).ready(() => {
    setupPageView();
    setupRenderReady({onLoaded: setupPageView});
    setupRootPageReady();
});
